import { paymentMethodLabel } from '@core/bookings/paymentWords';
import { accountKindWords } from '@core/cash/paidFrom';
import { methodsFor, setPart, type SplitPart } from '@core/cash/split';

import { controlClass } from './Field';

/** Which way the money goes, for the words a screen reader hears on each box. */
export type SplitDirection = 'from' | 'into' | 'outOf';

const boxWord: Record<SplitDirection, string> = {
  from: 'Paid from',
  into: 'Into',
  outOf: 'Out of',
};

/**
 * The account boxes on every website screen that moves money (canvases "Supplier payment and
 * choice", Pay B, and "Money in, by account", 2026-11-01) — the phone's `ui/SplitBoxes.tsx`: a row
 * per account with its name, kind and an amount box, and for a bank with money in it how (UPI,
 * card, transfer, cheque) and a reference. The rules are the core's `cash/split.ts`; the screen owns
 * the parts (`useSplitParts`).
 */
export function SplitBoxes({
  parts,
  onChange,
  direction,
  busy = false,
}: {
  parts: readonly SplitPart[];
  onChange: (next: (current: SplitPart[]) => SplitPart[]) => void;
  direction: SplitDirection;
  busy?: boolean;
}) {
  const change = (part: SplitPart, changes: Parameters<typeof setPart>[2]) =>
    onChange((current) => setPart(current, part.accountId, changes));

  return (
    <ul className="border-border-color m-0 flex list-none flex-col rounded-lg border ps-0">
      {parts.map((part) => {
        const filled = part.amount.trim() !== '';
        const methods = methodsFor(part.kind);
        const id = `split-${direction}-${part.accountId}`;

        return (
          <li
            key={part.accountId}
            className="border-border-color flex list-none flex-col gap-2 border-b px-3 py-2.5 last:border-b-0"
          >
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="mb-0 text-xs font-semibold text-gray-900">{part.accountName}</p>
                <p className="text-default mb-0 text-[11px]">
                  {filled
                    ? `${accountKindWords[part.kind]} · ${paymentMethodLabel[part.method]}`
                    : accountKindWords[part.kind]}
                </p>
              </div>
              <label className="sr-only" htmlFor={id}>
                {boxWord[direction]} {part.accountName}
              </label>
              <input
                id={id}
                inputMode="decimal"
                placeholder="0"
                value={part.amount}
                disabled={busy}
                onChange={(event) => change(part, { amount: event.target.value })}
                className={`${controlClass} font-mono-nums max-w-36 text-right ${
                  filled ? 'border-primary' : ''
                }`}
              />
            </div>
            {filled && methods.length > 1 ? (
              <div className="flex flex-wrap items-center gap-1.5">
                {methods.map((value) => (
                  <button
                    key={value}
                    type="button"
                    disabled={busy}
                    onClick={() => change(part, { method: value })}
                    className={`rounded border px-3 py-1.5 text-[12px] ${
                      part.method === value
                        ? 'border-primary bg-primary text-white'
                        : 'border-border-color text-default hover:bg-light'
                    }`}
                  >
                    {paymentMethodLabel[value]}
                  </button>
                ))}
                <input
                  aria-label={`${paymentMethodLabel[part.method]} reference for ${part.accountName}`}
                  placeholder={
                    part.method === 'Upi'
                      ? 'UPI reference (optional)'
                      : part.method === 'Cheque'
                        ? 'Cheque number (optional)'
                        : part.method === 'Card'
                          ? 'Slip number (optional)'
                          : 'UTR / reference (optional)'
                  }
                  value={part.reference}
                  disabled={busy}
                  onChange={(event) => change(part, { reference: event.target.value })}
                  className={`${controlClass} min-w-40 flex-1`}
                />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** The quick amounts under the boxes — "All ₹5,040", "Oldest bill · ₹6,500" — each filling one box. */
export function QuickAmounts({
  options,
  onPick,
  busy = false,
}: {
  options: readonly { label: string; amount: number }[];
  onPick: (amount: number) => void;
  busy?: boolean;
}) {
  if (options.length === 0) {
    return null;
  }

  return (
    <ul className="m-0 flex list-none flex-wrap gap-1.5 ps-0">
      {options.map((option) => (
        <li key={option.label} className="list-none">
          <button
            type="button"
            disabled={busy}
            onClick={() => onPick(option.amount)}
            className="border-border-color text-default hover:bg-light rounded border px-2.5 py-1 text-[12px]"
          >
            {option.label}
          </button>
        </li>
      ))}
    </ul>
  );
}
